import React, {useCallback, useEffect, useRef, useState} from 'react';
import ImageUpload from '../../resources/icon/img-upload.png'
import UploadHere from '../../resources/icon/upload.png'
import Forbidden from '../../resources/icon/forbidden.png'
import {deleteImagesAndFiles, getImagesAndFiles, uploadImagesAndFiles} from "../../api/file/FileService";
import ImageCarouselModal from "./ImageCarouselModal";
import AttachmentFileList from "./AttachmentFileList";
import AttachmentListUploader from "./AttachmentListUploader";

/**
 * `이미지업로더 `이미지
 * */


function ImageUploader({className, tableInfo, readOnly = false, displayMode = "thumbnail"}) {
    const isListMode = displayMode === "list";
    const [img, setImg] = useState([]);
    const [previewImages, setPreviewImages] = useState([]);
    const serverUrl = import.meta.env.VITE_IMG_BASE;

    async function getImg() {
        try {
            const response = await getImagesAndFiles(tableInfo.tableName, tableInfo.joinKey, "IMG");
            if(response.status===200){
                setImg(response.data);
                setPreviewImages(response.data.map((data) => (serverUrl + data.path + data.uploadName)));
            }
        }catch (e){

        }
    }

    useEffect(()=>{
        getImg();
    }, [])
    // 드래그 중일때와 아닐때의 스타일을 구분하기 위한 state 변수
    const [isDragging, setIsDragging] = useState(false);
    const [dragIn, setDragIn] = useState(false);
    // 각 선택했던 파일들의 고유값 id
    const fileId = useRef(0);
    // 드래그 이벤트를 감지하는 ref 참조변수 (label 태그에 들어갈 예정)
    const dragRef = useRef(null);
    const [openModal, setOpenModal] =useState(false);

    const [activeIndex, setActiveIndex] = useState(0);

    const fileUploadOrReturn = async (e) => {
        let selectFiles = [];
        if (tableInfo) {
            if (window.confirm("업로드하시겠습니까?")) {
                await uploadImagesAndFiles(e.type === "drop" ? e.dataTransfer.files : e.target.files, "", tableInfo);
                getImg();
            } else {
                return selectFiles;
            }
        } else {
            if (e.type === "drop") {
                selectFiles = Array.from(e.dataTransfer.files);
            } else {
                selectFiles = Array.from(e.target.files);
            }
        }

        return selectFiles;
    }

    const onChangeFiles = useCallback(async (e) => {
        let tempFiles = img.slice();
        const newPreviewImages = previewImages.slice();
        let selectFiles = await fileUploadOrReturn(e);

        for (const file of selectFiles) {
            const id = fileId.current++;

            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onload = (event) => {
                    const previewImage = event.target.result;
                    tempFiles.push({
                        id,
                        object: file,
                        previewImage,
                    });
                    setImg(tempFiles);
                    newPreviewImages.push(previewImage); // newPreviewImages에 새로운 미리보기 이미지를 추가합니다.
                    setPreviewImages(newPreviewImages);

                };
                reader.readAsDataURL(file);
            } else {
                alert("이미지만 첨부할 수 있습니다.")
            }
        }
    }, []);

    const handleDeleteImage = async (item, index) => {
        const target = item ?? img[index];
        if (!target) return;

        const deleteId = target.id;
        const removeIndex =
            typeof index === "number" ? index : img.findIndex((file) => file.id === deleteId);

        const newPreviewImages = previewImages.filter((_, i) => i !== removeIndex);
        setPreviewImages(newPreviewImages);

        const newFiles = img.filter((file) => file.id !== deleteId);
        setImg(newFiles);

        if (tableInfo) {
            if (window.confirm("삭제하시겠습니까?")) {
                await deleteImagesAndFiles(deleteId, tableInfo.tableName, tableInfo.joinKey);
            }
        }
    };

    const openImagePreview = (item, index) => {
        const openIndex = typeof index === "number" ? index : img.findIndex((file) => file.id === item?.id);
        setActiveIndex(openIndex >= 0 ? openIndex : 0);
        setOpenModal(true);
        document.body.style.overflow = "hidden";
    };
    const handleFileInputChange = (e) => {
        if (isListMode || !isDragging) {
            onChangeFiles(e);
        }
    };
    const handleDragIn = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
        setDragIn(true);
    }, []);

    const handleDragOut = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        setDragIn(false);
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer?.files.length > 0) {
            setIsDragging(true);
        } else {
            setIsDragging(false);
        }
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer?.files.length > 0) {
            setIsDragging(false);
            setDragIn(false);
            onChangeFiles(e);
        }
    }, [onChangeFiles]);


    const initDragEvents = useCallback(() => {
        if (dragRef.current !== null) {
            dragRef.current.addEventListener("dragenter", handleDragIn);
            dragRef.current.addEventListener("dragleave", handleDragOut);
            dragRef.current.addEventListener("dragover", handleDragOver);
            dragRef.current.addEventListener("drop", handleDrop);
        }
    }, [handleDragIn, handleDragOut, handleDragOver, handleDrop]);

    const resetDragEvents = useCallback(() => {
        // 앞서 말했던 4개의 이벤트에 Listener를 삭제합니다. (언마운트 될때)
        if (dragRef.current !== null) {
            dragRef.current.removeEventListener("dragenter", handleDragIn);
            dragRef.current.removeEventListener("dragleave", handleDragOut);
            dragRef.current.removeEventListener("dragover", handleDragOver);
            dragRef.current.removeEventListener("drop", handleDrop);
        }
    }, [handleDragIn, handleDragOut, handleDragOver, handleDrop]);

    useEffect(() => {
        if (isListMode) return undefined;
        initDragEvents();
        return () => resetDragEvents();
    }, [isListMode, initDragEvents, resetDragEvents]);

    if (isListMode) {
        return (
            <div
                className={`w-full relative ${className} mobile-hidden updateFile attachment-uploader--list`}
            >
                <AttachmentListUploader
                    readOnly={readOnly}
                    inputId="file_img"
                    inputAccept="image/*"
                    emptyLabel="첨부된 이미지가 없습니다."
                    onInputChange={handleFileInputChange}
                    hasFiles={img.length > 0}
                    fileList={
                        <AttachmentFileList
                            items={img}
                            variant="image"
                            readOnly={readOnly}
                            onRemove={handleDeleteImage}
                            onOpen={openImagePreview}
                        />
                    }
                />
                <ImageCarouselModal
                    openModal={openModal}
                    setOpenModal={setOpenModal}
                    files={img}
                    activeIndex={activeIndex}
                    setActiveIndex={setActiveIndex}
                />
            </div>
        );
    }

    const renderFileArea = () => {
        if (dragIn) {
            return (
                <>
                    <img src={UploadHere} className="w-[50px] inline-block mt-4 mb-4 drag-drop-img " alt="" />
                    <span className=" c-gray drag-drop-txt">여기에 놓으세요</span>
                </>
            );
        }

        return (
            <>
                {previewImages.map((previewImage, index) => (
                    <div className="uploaded-img mt-[20px]" key={index}>
                        {!readOnly && (
                            <div
                                className="delete-image print-hidden"
                                onClick={() => handleDeleteImage(img[index], index)}
                            >
                                <img src={Forbidden} alt="" />
                            </div>
                        )}
                        <img
                            key={index}
                            src={previewImage}
                            alt=""
                            className=""
                            onClick={() => openImagePreview(img[index], index)}
                        />
                    </div>
                ))}
                {previewImages[0] || readOnly ? (
                    ""
                ) : (
                    <>
                        <img
                            src={ImageUpload}
                            className="w-[50px] inline-block mt-4 mb-4 drag-drop-img print-hidden mobile-hidden "
                            alt=""
                        />
                        <span className=" c-gray drag-drop-txt print-hidden mobile-hidden">
                            파일을 끌어서 놓으세요
                        </span>
                    </>
                )}
            </>
        );
    };

    return (
        <div className={`w-full relative ${className} mobile-hidden updateFile`}>
            <div
                ref={dragRef}
                className={`print-image-container b-gray upload-h ${
                    isDragging ? "DragDrop-File-Dragging" : "DragDrop-File"
                }`}
            >
                {renderFileArea()}
            </div>
            {!readOnly && (
            <div className="py-3 bg-basic h-[40px] print-hidden">
                <label className="file-from-my px-6 py-1 b-gray bg-white item-center"
                       htmlFor={"file_img"}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"
                         className="w-4 h-4 inline-block mr-2">
                        <path
                            d="M9.25 13.25a.75.75 0 0 0 1.5 0V4.636l2.955 3.129a.75.75 0 0 0 1.09-1.03l-4.25-4.5a.75.75 0 0 0-1.09 0l-4.25 4.5a.75.75 0 1 0 1.09 1.03L9.25 4.636v8.614Z"/>
                        <path
                            d="M3.5 12.75a.75.75 0 0 0-1.5 0v2.5A2.75 2.75 0 0 0 4.75 18h10.5A2.75 2.75 0 0 0 18 15.25v-2.5a.75.75 0 0 0-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5Z"/>
                    </svg>
                    <span className={'line-h-22'}>내 PC에서 파일 찾기</span>
                </label>
                <input
                    className="hidden"
                    id={"file_img"}
                    type={"file"}
                    multiple={true}
                    onChange={handleFileInputChange}
                />

            </div>
            )}

            <ImageCarouselModal openModal={openModal} setOpenModal={setOpenModal} files={img} activeIndex={activeIndex} setActiveIndex={setActiveIndex}/>
        </div>
    );
}

export default ImageUploader;
